import { apiClient } from '@/lib/api/client'
import type {
  ArtistWithDetails,
  ArtistFullDetails,
  EmailSend,
  EmailPreferences,
  CreativeGenome,
  ArtistGenomeResponse,
} from '../types'

export interface GetArtistsParams {
  limit?: number
  offset?: number
  search?: string
  verified?: boolean
  hasArtworks?: boolean
  simple?: boolean // Use lightweight query (faster, no artwork counts)
}

export interface SendEmailPayload {
  subject: string
  htmlContent: string
  textContent?: string
  templateSlug?: string
}

export const artistAdminApi = {
  // Get all artists with filters
  getArtists: async (params: GetArtistsParams = {}): Promise<ArtistWithDetails[]> => {
    const response = await apiClient.get('/api/admin/communications/artists', { params })
    return response.data
  },

  // Get single artist with full details
  getArtist: async (artistId: number): Promise<ArtistFullDetails> => {
    const response = await apiClient.get(`/api/admin/communications/artists/${artistId}`)
    return response.data
  },

  // Get artist communication history
  getArtistCommunications: async (
    artistId: number,
    params: { limit?: number; offset?: number; emailType?: string } = {}
  ): Promise<EmailSend[]> => {
    const response = await apiClient.get(`/api/admin/communications/artists/${artistId}/history`, { params })
    return response.data
  },

  // Send email to single artist
  sendEmailToArtist: async (artistId: number, payload: SendEmailPayload): Promise<{ success: boolean; message: string; emailId?: string }> => {
    const response = await apiClient.post(`/api/admin/communications/artists/${artistId}/send-email`, payload)
    return response.data
  },

  // Update artist email preferences
  updateArtistEmailPreferences: async (
    artistId: number,
    preferences: Partial<EmailPreferences>
  ): Promise<EmailPreferences> => {
    const response = await apiClient.put(`/api/admin/communications/artists/${artistId}/email-preferences`, preferences)
    return response.data
  },

  // Get the artist's Creative Genome (design seed) + last-updated timestamp.
  // data is null when the artist exists but has no genome yet.
  getArtistGenome: async (artistId: number): Promise<ArtistGenomeResponse> => {
    const response = await apiClient.get<ArtistGenomeResponse>(
      `/api/admin/communications/artists/${artistId}/genome`
    )
    return response.data
  },

  // Upsert the artist's Creative Genome. Stored verbatim; propagates to the artist's
  // generated portfolio + Moments (portfolios reads Easel-first).
  updateArtistGenome: async (
    artistId: number,
    genome: CreativeGenome
  ): Promise<ArtistGenomeResponse> => {
    const response = await apiClient.put<ArtistGenomeResponse>(
      `/api/admin/communications/artists/${artistId}/genome`,
      genome
    )
    return response.data
  },
}
